import Feature from 'ol/Feature';
import { GEOMETRY } from '@utils/map/helpers';

import { DETECTION_POINTER, DETECTION_POINTERS, DEFAULT_DETECTION_POINTER } from '../constants';

/**
 * Feature building + source reconciliation for the collection map layers.
 *
 * Each detection is drawn as exactly one shape — its center point, bounding box,
 * or outline — chosen per object by the detection pointer. All three shape
 * layers are live at once and every feature lands in exactly one of them, so the
 * rendered feature count matches the number of visible detections.
 */

/**
 * Where to look when a detection can't produce the shape its pointer asks for —
 * the model may not have emitted an outline (SAM doesn't run in the v2
 * pipeline), or the geometry may be malformed.
 *
 * Ordered per pointer by how close the substitute is to what was asked for, so
 * a missing center point becomes a box rather than the largest shape available.
 */
const SHAPE_FALLBACK = {
	[DETECTION_POINTER.CENTER_POINT]: [DETECTION_POINTER.BOUNDING_BOX, DETECTION_POINTER.OUTLINE],
	[DETECTION_POINTER.BOUNDING_BOX]: [DETECTION_POINTER.OUTLINE, DETECTION_POINTER.CENTER_POINT],
	[DETECTION_POINTER.OUTLINE]: [DETECTION_POINTER.BOUNDING_BOX, DETECTION_POINTER.CENTER_POINT],
};

const isPosition = value =>
	Array.isArray(value) &&
	value.length >= 2 &&
	Number.isFinite(value[0]) &&
	Number.isFinite(value[1]);

// Ring/polygon checks sample the first entry rather than validating every vertex:
// enough to reject the malformed shapes OpenLayers accepts silently, and O(1) so
// availability can be derived for thousands of detections without building
// throwaway geometries.
const isRing = value => Array.isArray(value) && value.length >= 3 && isPosition(value[0]);
const isPolygon = value => Array.isArray(value) && isRing(value[0]);
const isMultiPolygon = value => Array.isArray(value) && isPolygon(value[0]);

/**
 * Normalizes the ways a polygon reaches us — a GeoJSON geometry object or a bare
 * coordinate ring — into `{ type, coordinates }`, or null if the coordinates
 * aren't a usable polygon.
 *
 * The `type` is carried through rather than assumed: a segmentation mask can be
 * a MultiPolygon, and MultiPolygon coordinates passed to `new Polygon` don't
 * throw — OpenLayers reads the extra nesting as higher-dimensional vertices and
 * silently produces an XYZM geometry that renders as garbage.
 */
const geometrySource = value => {
	if (!value) return null;
	const coordinates = Array.isArray(value) ? value : value.coordinates;
	const type =
		!Array.isArray(value) && value.type === 'MultiPolygon' ? 'MultiPolygon' : 'Polygon';
	const valid = type === 'MultiPolygon' ? isMultiPolygon(coordinates) : isPolygon(coordinates);
	return valid ? { type, coordinates } : null;
};

/**
 * Which part of a detection backs which shape — read by both the renderer and
 * the availability check, so the two can't disagree about what a detection can
 * draw.
 *
 * GeoJSON always carries a `geometry`, so it backs exactly one shape, chosen per
 * feature by whether a separate `bbox` is present:
 *   bbox present → the box is `properties.bbox`, `geometry` is the outline
 *   bbox absent  → `geometry` is the box, the outline is `mask_geometry` alone
 * If both polygon shapes fell back to `geometry`, every detection would report an
 * outline and the picker could never say a run produced none.
 *
 * @param {object} feature - A GeoJSON feature
 * @param {'center_point'|'bounding_box'|'outline'} shape
 * @returns {{ type: 'Point'|'Polygon'|'MultiPolygon', coordinates: Array }|null}
 */
export const shapeGeometrySpec = (feature, shape) => {
	const props = feature?.properties ?? {};

	if (shape === DETECTION_POINTER.CENTER_POINT) {
		const { center } = props;
		const coordinates = [center?.x, center?.y];
		return isPosition(coordinates) ? { type: 'Point', coordinates } : null;
	}

	const bbox = geometrySource(props.bbox);
	if (shape === DETECTION_POINTER.BOUNDING_BOX) {
		return bbox ?? geometrySource(feature?.geometry);
	}
	return geometrySource(props.mask_geometry) ?? (bbox ? geometrySource(feature?.geometry) : null);
};

/** Builds the OL geometry for one shape, or null when the detection can't produce it. */
export const buildShapeGeometry = (feature, shape) => {
	const spec = shapeGeometrySpec(feature, shape);
	if (!spec) return null;
	try {
		return new GEOMETRY[spec.type](spec.coordinates);
	} catch {
		return null; // malformed beyond what the structural checks catch
	}
};

/** Whether one detection can draw the given shape. */
export const hasShapeData = (feature, shape) => shapeGeometrySpec(feature, shape) !== null;

/**
 * The shape a detection will be drawn as: the one its pointer asks for, or the
 * closest usable substitute. Null when it carries no usable geometry at all, in
 * which case it isn't rendered.
 *
 * @param {object} feature - A GeoJSON feature
 * @param {'center_point'|'bounding_box'|'outline'} desired
 * @returns {'center_point'|'bounding_box'|'outline'|null}
 */
export const resolveShape = (feature, desired) => {
	if (hasShapeData(feature, desired)) return desired;
	const fallback = SHAPE_FALLBACK[desired] ?? SHAPE_FALLBACK[DEFAULT_DETECTION_POINTER];
	return fallback.find(shape => hasShapeData(feature, shape)) ?? null;
};

/**
 * Counts, per object key (node uuid), how many of its detections can draw each shape.
 *
 * Counts rather than booleans so the picker can tell "no detection of this object
 * has an outline" (offer it as unavailable) from "some do" (offer it, but say how
 * many) — with per-feature fallback in play, a boolean would let a user pick
 * Outline and silently get a mix of outlines and boxes.
 *
 * @param {Array} features - GeoJSON features
 * @returns {Object} { [node_uuid]: { total, center_point, bounding_box, outline } }
 */
export const buildShapeAvailability = features => {
	const byKey = {};
	for (const feature of features ?? []) {
		const key = feature?.properties?.node_uuid;
		if (key == null) continue;

		let entry = byKey[key];
		if (!entry) {
			entry = { total: 0, ...Object.fromEntries(DETECTION_POINTERS.map(s => [s, 0])) };
			byKey[key] = entry;
		}

		entry.total += 1;
		for (const shape of DETECTION_POINTERS) {
			if (hasShapeData(feature, shape)) entry[shape] += 1;
		}
	}
	return byKey;
};

/**
 * Splits the filtered set into one bucket per shape layer. Buckets are disjoint —
 * a feature that can't draw anything is dropped — so together they hold at most
 * as many features as were passed in, never more.
 *
 * @param {Array} filteredFeatures
 * @param {Object} pointerByKey - { [node_uuid]: pointer }
 * @returns {{ center_point: Array, bounding_box: Array, outline: Array }}
 */
export const bucketFeaturesByShape = (filteredFeatures, pointerByKey) => {
	const buckets = Object.fromEntries(DETECTION_POINTERS.map(shape => [shape, []]));

	for (const feature of filteredFeatures ?? []) {
		const desired = pointerByKey?.[feature?.properties?.node_uuid];
		const shape = resolveShape(feature, desired ?? DEFAULT_DETECTION_POINTER);
		if (shape) buckets[shape].push(feature);
	}

	return buckets;
};

/**
 * Builds a single OL Feature for one GeoJSON feature and one shape, or null when
 * the source feature can't produce that shape. Properties are copied on so
 * hover/click interactions can read them later.
 *
 * @param {object} feature - A GeoJSON feature (EPSG:3857)
 * @param {'center_point'|'bounding_box'|'outline'} shape
 * @returns {Feature|null}
 */
export const buildShapeFeature = (feature, shape) => {
	const geometry = buildShapeGeometry(feature, shape);
	if (!geometry) return null;
	return new Feature({ geometry, ...(feature.properties ?? {}) });
};

/**
 * Reconciles a shape layer's VectorSource against its desired set by diffing on
 * the GeoJSON feature reference: adds only what is newly present, removes only
 * what left, leaves the rest untouched — so unchanged features never blink.
 * `filterFeatures` preserves source references (no cloning), which is what makes
 * the feature object a reliable identity key.
 *
 * This is also how a feature moves between layers when its pointer changes.
 * Callers must reconcile all three shapes in one synchronous pass, or a migrating
 * feature is briefly in both sources (drawn twice) or in neither (blinks).
 *
 * The source is mutated in place — never `setSource(...)` — so the WebGL
 * renderer's listeners stay attached to the instance they were initialized with.
 *
 * @param {import('ol/source/Vector').default} source
 * @param {Map<object, Feature>} rendered - GeoJSON feature → built OL Feature
 * @param {Array} filteredFeatures - desired filtered GeoJSON features for this shape
 * @param {'center_point'|'bounding_box'|'outline'} shape
 */
export const reconcileShapeSource = (source, rendered, filteredFeatures, shape) => {
	const desired = new Set();
	const toAdd = [];

	for (const feature of filteredFeatures ?? []) {
		if (rendered.has(feature)) {
			desired.add(feature);
			continue;
		}
		const olFeature = buildShapeFeature(feature, shape);
		if (olFeature) {
			rendered.set(feature, olFeature);
			desired.add(feature);
			toAdd.push(olFeature);
		}
	}

	for (const [feature, olFeature] of rendered) {
		if (!desired.has(feature)) {
			source.removeFeature(olFeature);
			rendered.delete(feature);
		}
	}

	if (toAdd.length) source.addFeatures(toAdd);
};
